// lib
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import * as R from "ramda";
import {
  Button,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from "@material-ui/core";
import { MoreVert, Search } from "@material-ui/icons";
// src
import { MY_PLANNING_ID, PresetSection } from "config/constants";
import { capitalizeFirstLetter } from "utils/string";
import PresetMenu from "containers/plannings/partials/presetPicker/PresetMenu";
import ColoredCheckbox from "./partials/ColoredCheckbox";
import Section from "./partials/Section";
import ResourceIcon from "./partials/ResourceIcon";

const countInEnableds = (presets, enabledIds) => {
  return presets.filter(({ id }) => enabledIds.includes(id)).length;
};

const titleWithCount = (prefix, presets, enabledIds) => {
  const count = countInEnableds(presets, enabledIds);
  return count ? (
    <>
      <span>{prefix}</span>
      <span className="count">{`(${count})`}</span>
    </>
  ) : (
    prefix
  );
};

const PresetPicker = ({
  // data props
  isIdle,
  isLogged,
  locals,
  favorites,
  favoriteIds,
  favoritesFloor,
  favoritesGroups,
  coursesGroups,
  roomsGroups,
  enableds,
  selfTranslate,
  // dispatch props
  disableCategory,
  enableCategory,
  disablePreset,
  enablePreset,
  showFavorites,
  showSearch,
}) => {
  const [openMenuWithPreset, setOpenMenuWithPreset] = useState(null);
  const [openMenuWithSection, setOpenMenuWithSection] = useState(null);
  const setOpenMenuWith = (preset, section) => {
    setOpenMenuWithPreset(preset);
    setOpenMenuWithSection(section);
  };
  const [anchorEl, setAnchorEl] = useState(null);
  useEffect(() => setOpenMenuWith(null, null), [isIdle]);
  const { t } = useTranslation();

  const enabledIds = enableds.map(({ id }) => id);

  const isEnabled = (target) => {
    const id = typeof target === "object" ? target.id : target;
    return R.any((i) => i === id, enabledIds);
  };

  // some presets may be listed as both favorite and popular
  const isExpanded = (id, section) => {
    if (section === PresetSection.POPULAR) {
      const popularEnableds = enableds.filter(({ categoryId }) => categoryId === id);
      const allInFavorites = R.all((pop) => favoriteIds.includes(pop.id), popularEnableds);
      return !allInFavorites;
    }
    // other sections
    return R.any(({ underId }) => underId === id, enableds);
  };

  const createHandleToggle = ({ id }) => () =>
    isEnabled(id) ? disablePreset(id) : enablePreset(id);

  // returning new enabled status is a hack to control accordion section
  const createHandleToggleForCategory = ({ id }) => () => {
    const enabled = isEnabled(id);
    if (enabled) {
      disableCategory(id);
    } else {
      enableCategory(id);
    }
    return !enabled;
  };

  const handleOpenPresetMenu = (event, preset, section) => {
    setAnchorEl(event.currentTarget);
    setOpenMenuWith(preset, section);
  };

  const handleClosePresetMenu = () => {
    setOpenMenuWith(null, null);
  };

  const handleOpenSearch = () => showSearch();

  const handleOpenFavorites = () => showFavorites();

  const optionsClassName = (p) => (p === openMenuWithPreset ? "options selected" : "options");

  const renderItem = (p, s) => (
    <div key={p.id} className="preset">
      <ListItem button onClick={createHandleToggle(p)}>
        <ListItemIcon>
          <ColoredCheckbox color={p.color} className="checkbox" checked={isEnabled(p)} />
        </ListItemIcon>
        <ListItemText>
          <ResourceIcon preset={p} />
          <span>{selfTranslate(p.name)}</span>
        </ListItemText>
        {p.id !== MY_PLANNING_ID && (
          <ListItemSecondaryAction className={optionsClassName(p)}>
            <IconButton
              onClick={(e) => handleOpenPresetMenu(e, p, s)}
              size="small"
              edge="end"
              aria-label="options"
            >
              <MoreVert />
            </IconButton>
          </ListItemSecondaryAction>
        )}
      </ListItem>
    </div>
  );

  return (
    <div className="app-presets">
      {isLogged && (
        <>
          <div className="preset-search-button">
            <Button onClick={handleOpenSearch} startIcon={<Search />} disableElevation>
              {t("menu.search")}
            </Button>
          </div>
        </>
      )}
      {locals.length > 0 && (
        <Section title={titleWithCount(t("menu.locals"), locals, enabledIds)} defaultExpanded>
          {locals.filter((p) => p.name).map((p) => renderItem(p, PresetSection.LOCAL))}
        </Section>
      )}
      {isLogged && (
        <Section
          appendClassName="favorites"
          title={titleWithCount(t("menu.favorites"), favorites, enabledIds)}
          secondaryAction={handleOpenFavorites}
          defaultExpanded
        >
          {favoritesFloor.map((p) => renderItem(p, PresetSection.FAVORITE))}
          {favoritesGroups.map(({ parent, children }) => {
            return (
              <Section
                level={1}
                key={parent.id}
                title={titleWithCount(parent.name, children, enabledIds)}
                defaultExpanded={isExpanded(parent.id)}
                deferClick={createHandleToggleForCategory(parent)}
                checked={isEnabled(parent)}
              >
                {children.map((p) => renderItem(p, PresetSection.FAVORITE))}
              </Section>
            );
          })}
        </Section>
      )}

      <Section
        title={titleWithCount(
          capitalizeFirstLetter(t("resources.courses")),
          coursesGroups,
          enabledIds,
        )}
        defaultExpanded
      >
        {coursesGroups.map(({ parent, children }) => {
          return (
            <Section
              level={1}
              key={parent.id}
              title={titleWithCount(parent.name, children, enabledIds)}
              defaultExpanded={isExpanded(parent.id)}
            >
              {children.map((p) => renderItem(p, PresetSection.POPULAR))}
            </Section>
          );
        })}
      </Section>

      <Section
        title={titleWithCount(capitalizeFirstLetter(t("resources.rooms")), roomsGroups, enabledIds)}
        defaultExpanded
      >
        {roomsGroups.map(({ parent, children }) => {
          return (
            <Section
              level={1}
              key={parent.id}
              title={titleWithCount(parent.name, children, enabledIds)}
              defaultExpanded={isExpanded(parent.id, PresetSection.POPULAR)}
            >
              {children.map((p) => renderItem(p, PresetSection.POPULAR))}
            </Section>
          );
        })}
      </Section>

      <PresetMenu
        anchorEl={anchorEl}
        preset={openMenuWithPreset}
        section={openMenuWithSection}
        open={Boolean(openMenuWithPreset)}
        onClose={handleClosePresetMenu}
      />
    </div>
  );
};

PresetPicker.propTypes = {
  // data props
  isIdle: PropTypes.bool.isRequired,
  isLogged: PropTypes.bool.isRequired,
  locals: PropTypes.array.isRequired,
  favorites: PropTypes.array.isRequired,
  favoriteIds: PropTypes.array.isRequired,
  favoritesFloor: PropTypes.array.isRequired,
  favoritesGroups: PropTypes.array.isRequired,
  coursesGroups: PropTypes.array.isRequired,
  roomsGroups: PropTypes.array.isRequired,
  enableds: PropTypes.array.isRequired,
  selfTranslate: PropTypes.func.isRequired,
  // dispatch props
  disableCategory: PropTypes.func.isRequired,
  enableCategory: PropTypes.func.isRequired,
  disablePreset: PropTypes.func.isRequired,
  enablePreset: PropTypes.func.isRequired,
  showFavorites: PropTypes.func.isRequired,
  showSearch: PropTypes.func.isRequired,
};

export default PresetPicker;
